import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlineRounded";
import { useMemo, useState } from "react";
import type { PortalWidget } from "@entrilia/shared/api/portalWidgetsTypes";
import DateRangeSelect from "@entrilia/shared/components/DateRangeSelect";
import MultiSelectFilter from "@entrilia/shared/components/MultiSelectFilter";
import CompaniesIcon from "@entrilia/shared/icons/CompaniesIcon";
import type { DescriptiveDateRange } from "@entrilia/shared/utilities/dateFilterHelper";
import {
  anytime,
  lastMonth,
  lastQuarter,
  lastYear,
  next30Days,
  nextQuarter,
  nextSevenDays,
  thisMonth,
  thisQuarter,
  thisYear,
  today,
} from "@entrilia/shared/utilities/dateFilterHelper";
import type { CashFlowPaymentStatus } from "../../../api/types/widgetTypes";
import { allCashFlowPaymentStatuses } from "../../../api/types/widgetTypes";
import { cashFlowPaymentStatusCaptionMap } from "../../../utilities/enumCaptions";
import SwipeableFiltersDrawer from "../../common/reports/mobile/SwipeableFiltersDrawer";
import { useCashFlowsWidgetData } from "../widgetHooks";
import WidgetListDialog from "./WidgetListDialog";
import WidgetMobileContent from "./WidgetMobileContent";

interface Props {
  widgetSettings: PortalWidget;
  onClose: () => void;
}

const dateRanges: DescriptiveDateRange[] = [
  anytime,
  today,
  nextSevenDays,
  next30Days,
  nextQuarter,
  thisMonth,
  thisQuarter,
  thisYear,
  lastMonth,
  lastQuarter,
  lastYear,
];

const CashFlowsWidgetListDialog = ({ widgetSettings, onClose }: Props) => {
  const {
    parameters,
    report,
    fetchError,
    filteredRows,
    isRunningReport,
    setSearchValue,
    investorIds,
    setInvestorIds,
    fundIds,
    setFundIds,
    paymentStatuses,
    setPaymentStatuses,
    dateRange,
    setDateRange,
    clearFilters,
    isAnyFilterApplied,
  } = useCashFlowsWidgetData(widgetSettings);

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const filterText = useMemo(() => {
    const investorNames =
      investorIds.length > 0 && parameters?.investors
        ? parameters.investors
            .filter((inv) => investorIds.includes(inv.id))
            .map((inv) => inv.name)
            .join(", ")
        : "";

    const fundNames =
      fundIds.length > 0 && parameters?.funds
        ? parameters.funds
            .filter((fund) => fundIds.includes(fund.id))
            .map((fund) => fund.name)
            .join(", ")
        : "";

    const statusNames =
      paymentStatuses.length > 0
        ? paymentStatuses.map((status) => cashFlowPaymentStatusCaptionMap[status]).join(", ")
        : "";

    return [investorNames, fundNames, statusNames].filter((part) => part !== "").join(" | ");
  }, [fundIds, investorIds, parameters, paymentStatuses]);

  return (
    <>
      <WidgetListDialog
        widgetSettings={widgetSettings}
        onSearch={setSearchValue}
        onOpenFilters={() => setIsFiltersOpen(true)}
        onClose={onClose}
        rowCount={filteredRows.length}
        loading={isRunningReport}
        isAnyFilterApplied={isAnyFilterApplied}
        filterText={filterText}
      >
        <WidgetMobileContent report={report} rows={filteredRows} loading={isRunningReport} fetchError={fetchError} />
      </WidgetListDialog>

      <SwipeableFiltersDrawer
        open={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        isAnyFilterApplied={isAnyFilterApplied}
        onClearFilters={clearFilters}
        disabled={isRunningReport}
      >
        <MultiSelectFilter
          options={parameters?.investors ?? []}
          selectedValues={investorIds}
          onChange={setInvestorIds}
          getOptionValue={(inv) => inv.id}
          getOptionLabel={(inv) => inv.name}
          placeholder="Investors"
          StartIcon={PersonIcon}
          disabled={isRunningReport || !parameters?.investors.length}
          fullWidth
        />

        <MultiSelectFilter
          options={parameters?.funds ?? []}
          selectedValues={fundIds}
          onChange={setFundIds}
          getOptionValue={(fund) => fund.id}
          getOptionLabel={(fund) => fund.name}
          placeholder="Funds"
          StartIcon={CompaniesIcon}
          disabled={isRunningReport || !parameters?.funds.length}
          fullWidth
        />

        <MultiSelectFilter<CashFlowPaymentStatus>
          options={[...allCashFlowPaymentStatuses]}
          selectedValues={paymentStatuses}
          onChange={(statuses) => setPaymentStatuses(statuses as CashFlowPaymentStatus[])}
          getOptionValue={(status) => status}
          getOptionLabel={(status) => cashFlowPaymentStatusCaptionMap[status]}
          placeholder="Status"
          StartIcon={AccountBalanceWalletIcon}
          disabled={isRunningReport}
          fullWidth
        />

        <DateRangeSelect
          dateRange={dateRange}
          onChange={setDateRange}
          predefinedRanges={dateRanges}
          placeholder="Due Date"
          disabled={isRunningReport}
          fullWidth
        />
      </SwipeableFiltersDrawer>
    </>
  );
};

export default CashFlowsWidgetListDialog;
